import { GENERAL_ERROR_MESSAGE } from "./message";

// Define constant keys for localStorage items
export const TOKEN_KEY = "token";
export const EMAIL_KEY = "email";
export const TOKEN_EXPIRATION_KEY = "tokenExpiration";
export const ROLES_KEY = "roles";
export const USER_NAME = "userName";
export const USER_PROFILE = "userProfile";
export const USER_ID = "userId";
export const USER_CREDENTIALS = "userCredentials";
export const SET_USER_EMAIL = "setUserEmail";
export const IS_CUSTOMER_KEY = "isCustomer";
export const AGENT_RATE_NAME = "agentRateName";
export const CUSTOMER_RATE_NAME = "customerRateName";
export const IS_USER_OF_OMNI_MOVE = "isUserOfOmniMove";
export const CUSTOMER_ID = "customerId";
export const CUSTOMER_NAME = "customerName";
export const LOCAL_STORAGE_KEY = "locationStateData";
export const IS_DIRECT_CUTOMER = "isDirectCustomer";
export const IS_USER_OF_DIRECT_CUSTOMER = "isUserOfDirectCustomer";
export const CUSTOMER_LEVEL = "customerLevel";
export const PARENT_CUSTOMER_ID = "parentCustomerId";
export const COMPANY_LOGO = "companyLogo";
export const AGENT_ID = "agentId";
export const AGENT_TYPE = "agentType";
export const DEV_REFER = "devRefer";
export const CREATE_AGENT_PERMISSION_KEY = "createAgentPermission";
export const SESSION_ID_KEY = "sessionId";
export const ROLE = "role";

// Clear all relevant items from localStorage
export const clearTokenFromLocalStorage = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EMAIL_KEY);
  localStorage.removeItem(TOKEN_EXPIRATION_KEY);
  localStorage.removeItem(ROLES_KEY);
  localStorage.removeItem(USER_NAME);
  localStorage.removeItem(USER_PROFILE);
  localStorage.removeItem(USER_ID);
  localStorage.removeItem(IS_CUSTOMER_KEY);
  // AGENT_RATE_NAME / CUSTOMER_RATE_NAME are per-tab title state held in
  // sessionStorage (see Navbar). ltlIntegrationEdit is per-tab edit-mode state.
  sessionStorage.removeItem(AGENT_RATE_NAME);
  sessionStorage.removeItem(CUSTOMER_RATE_NAME);
  sessionStorage.removeItem("ltlIntegrationEdit");
  localStorage.removeItem(IS_USER_OF_OMNI_MOVE);
  localStorage.removeItem(CUSTOMER_ID);
  localStorage.removeItem(CUSTOMER_NAME);
  localStorage.removeItem(LOCAL_STORAGE_KEY);
  localStorage.removeItem(IS_DIRECT_CUTOMER);
  localStorage.removeItem(IS_USER_OF_DIRECT_CUSTOMER);
  localStorage.removeItem(CUSTOMER_LEVEL);
  localStorage.removeItem(DEV_REFER);
  localStorage.removeItem(AGENT_TYPE);
  localStorage.removeItem(CREATE_AGENT_PERMISSION_KEY);
  localStorage.removeItem(SESSION_ID_KEY);
};

// Check if the token has expired
export const checkTokenExpiration = () => {
  const tokenExpiration = localStorage.getItem(TOKEN_EXPIRATION_KEY);
  const now = Date.now();

  if (tokenExpiration) {
    const expiryTimestamp = parseInt(tokenExpiration, 10);
    if (now > expiryTimestamp) {
      // Token expired
      return true;
    }
  }
  // Token not expired
  return false;
};

// Get the reCAPTCHA site key from environment variables
export const SiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY as string;

// Retrieve the token from localStorage
export const AuthUtility = (): string => {
  return localStorage.getItem(TOKEN_KEY) ?? "";
};
// Types for session verification
export interface SessionVerificationOptions {
  dispatch: any; // AppDispatch type
  setLoading?: (loading: boolean) => void;
  setMessage?: (message: string, type: "success" | "error" | "info" | "warning") => void;
  navigate?: (path: string) => void;
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  fallbackEmail?: string;
}

/**
 * Verifies the session token from URL parameter
 * This is a reusable function that can be used in Login component or routes
 * @param sessionId - The session ID from URL
 * @param options - Configuration options including dispatch, callbacks, etc.
 */
export const verifySessionToken = async (
  sessionId: string,
  options: SessionVerificationOptions
): Promise<boolean> => {
  const {
    dispatch,
    setLoading,
    setMessage,
    navigate,
    onSuccess,
    onError,
    fallbackEmail = ""
  } = options;

  try {
    if (setLoading) setLoading(true);
    // Import dynamically to avoid circular dependencies
    const { postApi } = await import("../apis/api");
    const { VERIFY_SESSION_TOKEN_API } = await import("../apis/end-points");
    const { loginUser, setCustomerLevel, setDirectCustomer, setIsCUSTOMER, setIsUserOfOmniMove } = await import("../redux/reducer/auth/authSlice");
    const { setUserInfo, setComanyLogo } = await import("../redux/reducer/navbar/navbarSlice");
    const { GENERAL_ERROR_MESSAGE } = await import("./message");

    const apiResponse = await postApi(VERIFY_SESSION_TOKEN_API, {
      sessionId: sessionId,
    });


    if (apiResponse.success) {
      const data: any = apiResponse.data;
      localStorage.removeItem(SET_USER_EMAIL);
      // Save sessionId to localStorage
      localStorage.setItem(SESSION_ID_KEY, sessionId);

      const isCustomer =
        (data.role.name === "admin" ||
          data.role.name === "customersuperadmin") &&
        data?.isCustomerAdmin;
      const isUserOfOmniMove = data?.isUserOfOmniMove || false;
      const customer = data.customer;
      const agent = data.agent;

      dispatch(
        loginUser({
          userRole: data?.role?.name || "",
          token: data?.access_token || null,
          email: data?.email || fallbackEmail,
          expiration: Date.now() / 1000,
          customer: customer,
          parentCustomerId: data?.parentCustomerId,
          agent: agent,
          agentType: agent?.isInternalLTL ? "LTL" : null,
          createAgentPermission: data?.parentCustomer?.createAgentPermission,
        })
      );
      dispatch(
        setUserInfo({
          username: `${data?.firstName} ${data?.lastName}`,
          userId: data?.id || "",
          userEmail: data?.email || "",
          userRole: data?.role?.name || "",
          userProfile: `${data?.firstName?.[0] || ""}${data?.lastName?.[0] || ""}`,
        })
      );
      dispatch(
        setDirectCustomer({
          isDirectCustomer: data?.isDirectCustomer ?? false,
          isUserOfDirectCustomer: data?.isUserOfDirectCustomer ?? false,
        })
      );
      dispatch(setCustomerLevel(data?.customerLevel));
      dispatch(setIsCUSTOMER(isCustomer));
      dispatch(setIsUserOfOmniMove(isUserOfOmniMove));
      dispatch(setComanyLogo(customer?.logo));

      if (onSuccess) {
        onSuccess(data);
      }

      if (navigate) {
        navigate("/");
      }

      return true;
    } else {
      const errorMessage = apiResponse.error?.message || GENERAL_ERROR_MESSAGE;

      if (setMessage) {
        setMessage(errorMessage, "error");
      }

      if (onError) {
        onError(errorMessage);
      }

      return false;
    }
  } catch (error) {
    const errorMessage = GENERAL_ERROR_MESSAGE || "Session verification failed";
    console.error(errorMessage, error);

    if (setMessage) {
      setMessage(errorMessage, "error");
    }

    if (onError) {
      onError(errorMessage);
    }

    return false;
  } finally {
    if (setLoading) setLoading(false);
  }
};


export const redirectToLogin = () => {
  if (process.env.REACT_APP_ENV !== "local") {
    window.location.href = process.env.REACT_APP_REDIRECT_URL || "/users/sign_in";
  } else {
    window.location.href = "/users/sign_in";
  }
};

